import React from 'react';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import { When } from 'react-if';
import { GlobalToken, theme } from 'antd';

import { useStores } from '@/hooks/use-stores';
import { useMeasurement } from '@/hooks/useMeasurement';
import { useValueByConnectivity } from '@/hooks/useValueByConnectivity';
import DissolvedOxygenIcon from '@/comp/common/icons/DissolvedOxygenIcon';
import WidgetWithIcon from '@/comp/common/icons/WidgetWithIcon';
import { AlertsWidget } from '@/comp/MapBuilder/components/AlertsWidget';
import { Ports } from '@/comp/MapBuilder/components/Ports';
import { CustomThemeToken } from '@/theme';

import ConnectivityLight from '../ConnectivityLight';
import { getWidgetBorderColor, isSelectedNodeOnMap } from '../../utils/utils';

const { useToken } = theme;

export const DissolvedOxygenSensorNodeWidget = observer(
  ({ node, engine = null, width, height, renderedOnMap = false }) => {
    const { tunit, tvalue } = useMeasurement();
    const { tcunit, tcvalue } = useValueByConnectivity();
    const { connectivity, dissolvedOxygenPPM } = node.getEntity();
    const { mapBuilderStore } = useStores();
    const { token } = useToken() as unknown as { token: CustomThemeToken };

    const isSelected = isSelectedNodeOnMap(
      node.device.deviceId,
      mapBuilderStore.selectedDevice?.deviceId,
      renderedOnMap,
    );

    const borderColor = getWidgetBorderColor(isSelected, node.getEntity().inMaintenance, renderedOnMap, token);

    return (
      <Container width={width} height={height}>
        <WidgetWithIcon
          width={width}
          height={height}
          isSelected={isSelected}
          borderColor={borderColor}
          ConnectivityIcon={
            <ConnectivityLight
              connectivity={connectivity}
              inMaintenance={node.getEntity().inMaintenance}
              Icon={DissolvedOxygenIcon}
              token={token}
            />
          }
        />

        <When condition={!!engine}>
          <div className="value-container">
            <span className="value">{tcvalue(tvalue(dissolvedOxygenPPM, 'ppm'), connectivity)}</span>
            <span className="label">{tcunit(tunit('ppm'), dissolvedOxygenPPM, connectivity)}</span>
          </div>

          <AlertsWidget alerts={node.alerts} />

          <Ports node={node} engine={engine} width={width} height={height} />
        </When>
      </Container>
    );
  },
);

interface ContainerProps {
  theme: { token: GlobalToken };
  width: number;
  height: number;
}

const Container = styled.div<ContainerProps>`
  position: relative;
  width: ${({ width }) => width}px;
  height: ${({ height }) => height}px;
  text-align: center;

  .value-container {
    display: flex;
    align-items: baseline;
    position: absolute;
    top: -30px;
    left: 50%;
    transform: translateX(-50%);
    text-align: center;

    .value {
      flex-shrink: 0;
      font-size: ${({ theme: { token } }) => token.fontSizeXL}px;
      font-weight: ${({ theme: { token } }) => token.fontWeightStrong};
    }

    .label {
      flex-shrink: 0;
      font-size: ${({ theme: { token } }) => token.fontSize}px;
      color: ${({ theme: { token } }) => token.colorTextTertiary};
      margin-left: ${({ theme: { token } }) => token.marginXXS}px;
    }
  }
`;
